import type { ContractColumn, DataContract, Snapshots } from "./data-contracts";
import type { Row } from "./types";

// These are explicit demo extensions, not claims about production Prisma fields.
type Interval = { startedAt: string; endedAt: string | null };
export interface TehoShift extends Interval {
  id: string;
  employeeId: string;
  employeeName: string;
  propertyId: string;
  propertyName: string;
  operatingUnitId: string;
  workDate: string;
  breaks: (Interval & { type: "meal" | "coffee" })[];
}
export interface CleanerShift extends TehoShift {
  hourlyRateCents: number | null;
}
export interface TehoCleaningTask extends Interval {
  id: string;
  unitName: string;
  priceCents: number | null;
  currency: string;
  participants: (Interval & { shiftId: string })[];
}
export interface DriverShift extends TehoShift {
  plannedKm: number | null;
  actualKm: number | null;
}
export interface DeliveryRequest extends Interval {
  id: string;
  shiftId: string;
  deadline: string | null;
  urgent: boolean;
  dogHelp: boolean;
  cancelled: boolean;
  waitingObserved: boolean;
}
export interface DriverTrip extends Interval {
  id: string;
  requestId: string;
  kilometres: number | null;
}
export interface CleanerWait extends Interval {
  id: string;
  requestId: string;
  cleanerId: string;
}
export interface TehoRecords {
  cleanerShifts: CleanerShift[];
  cleaningTasks: TehoCleaningTask[];
  driverShifts: DriverShift[];
  requests: DeliveryRequest[];
  trips: DriverTrip[];
  waits: CleanerWait[];
}
const column = (
  type: ContractColumn["type"],
  description: string,
  nullable = false,
): ContractColumn => ({
  type,
  nullable,
  description,
  lineage: "Synthetic TEHO extension: " + description,
});
const common = {
  id: column("VARCHAR", "Unique fact identity"),
  operating_unit_id: column("VARCHAR", "Operating Unit identity"),
  property_id: column("VARCHAR", "Single property owning the shift"),
  property_name: column("VARCHAR", "Property display name"),
  work_date: column("DATE", "Owning shift's local work date"),
  snapshot_at: column("TIMESTAMP", "Fixed evidence cutoff"),
};
const shift = {
  ...common,
  employee_id: column("VARCHAR", "Employee identity"),
  employee_name: column("VARCHAR", "Employee display name"),
  started_at: column("TIMESTAMP", "Shift start"),
  ended_at: column("TIMESTAMP", "Closed shift end by snapshot", true),
  elapsed_minutes: column("DOUBLE", "Closed full-shift elapsed minutes", true),
  meal_minutes: column("DOUBLE", "Union of lunch intervals bounded to shift"),
  coffee_minutes: column(
    "DOUBLE",
    "Union of coffee intervals, included in pay",
  ),
  closed: column("BOOLEAN", "Valid closed shift at snapshot"),
  evidence_complete: column(
    "BOOLEAN",
    "Required evidence is complete and valid",
  ),
};
function contract(
  source: string,
  label: string,
  grain: string,
  columns: DataContract["columns"],
): DataContract {
  return { source, label, grain, key: "id", columns };
}
export const TEHO_CONTRACTS: Record<string, DataContract> = {
  mv_teho_cleaning_tasks: contract(
    "teho_cleaning_tasks",
    "TEHO cleaning prices",
    "One synthetic priced cleaning task",
    {
      ...common,
      unit_name: column("VARCHAR", "House/unit display name"),
      price_cents: column("INTEGER", "Immutable task price in EUR cents", true),
      currency: column("VARCHAR", "Task price currency; requires EUR"),
      completed: column("BOOLEAN", "Task completed by snapshot"),
      total_paid_task_minutes: column(
        "DOUBLE",
        "All participants' bounded task minutes less lunch",
      ),
    },
  ),
  mv_teho_cleaner_allocations: contract(
    "teho_cleaner_allocations",
    "TEHO cleaner task allocations",
    "One task/shift participant",
    {
      ...common,
      shift_id: column("VARCHAR", "Owning cleaner shift"),
      task_id: column("VARCHAR", "Priced cleaning task"),
      elapsed_minutes: column("DOUBLE", "Closed participant task span"),
      meal_minutes: column("DOUBLE", "Lunch overlapping the participant span"),
      prior_paid_task_minutes: column(
        "DOUBLE",
        "Cumulative paid task minutes before this participant in source order; supports conserving allocated cents",
      ),
      eligible: column(
        "BOOLEAN",
        "Participant, task price and full shift are eligible",
      ),
    },
  ),
  mv_cleaner_teho: contract(
    "cleaner_teho",
    "Cleaner TEHO shifts",
    "One synthetic cleaner paid shift",
    {
      ...shift,
      hourly_rate_cents: column(
        "INTEGER",
        "Hourly wage snapshot in EUR cents",
        true,
      ),
    },
  ),
  mv_driver_trips: contract(
    "driver_trips",
    "Driver physical trips",
    "One physical delivery attempt",
    {
      ...common,
      request_id: column("VARCHAR", "Owning delivery request"),
      started_at: column("TIMESTAMP", "Physical trip start"),
      ended_at: column("TIMESTAMP", "Physical trip end by snapshot", true),
      attempt_number: column(
        "INTEGER",
        "Chronological physical attempt number for request",
      ),
      kilometres: column("DOUBLE", "Observed trip kilometres", true),
    },
  ),
  mv_cleaner_waits: contract(
    "cleaner_waits",
    "Cleaner waiting evidence",
    "One disjoint attributed cleaner waiting interval",
    {
      ...common,
      request_id: column("VARCHAR", "Delivery causing the waiting"),
      cleaner_id: column("VARCHAR", "Waiting cleaner identity"),
      started_at: column("TIMESTAMP", "Bounded waiting start"),
      ended_at: column("TIMESTAMP", "Bounded waiting end"),
      wait_minutes: column(
        "DOUBLE",
        "Waiting person-minutes, overlap merged per cleaner across requests and driver shifts",
      ),
    },
  ),
  mv_driver_requests: contract(
    "driver_requests",
    "Driver delivery requests",
    "One delivery demand, regardless of trip count",
    {
      ...common,
      shift_id: column("VARCHAR", "Owning driver shift"),
      created_at: column("TIMESTAMP", "Request creation and SLA clock start"),
      completed_at: column(
        "TIMESTAMP",
        "Delivery completion by snapshot",
        true,
      ),
      deadline: column("TIMESTAMP", "Explicit request SLA deadline", true),
      completed: column("BOOLEAN", "Completed non-cancelled delivery"),
      due: column("BOOLEAN", "Non-cancelled request due by snapshot"),
      on_time: column(
        "BOOLEAN",
        "Due delivery completed no later than deadline",
      ),
      urgent: column("BOOLEAN", "Urgent request"),
      dog_help: column(
        "BOOLEAN",
        "Combined dog/extra-assistance workload category",
      ),
      cancelled: column(
        "BOOLEAN",
        "Cancelled request excluded from delivery populations",
      ),
      evidence_complete: column(
        "BOOLEAN",
        "Deadline, wait observation and trip evidence are valid",
      ),
    },
  ),
  mv_driver_teho: contract(
    "driver_teho",
    "Driver TEHO shifts",
    "One synthetic driver shift/route",
    {
      ...shift,
      planned_km: column(
        "DOUBLE",
        "Planned kilometres for the same route population",
        true,
      ),
      actual_km: column(
        "DOUBLE",
        "Observed kilometres including repeated trips",
        true,
      ),
    },
  ),
};

const time = (value: string | null) =>
  value === null ? NaN : Date.parse(value);
const validCents = (value: number | null) =>
  value !== null && Number.isSafeInteger(value) && value >= 0;
const validDistance = (value: number | null) =>
  value !== null && Number.isFinite(value) && value >= 0;
function merged(intervals: [number, number][]): [number, number][] {
  const result: [number, number][] = [];
  for (const [start, end] of intervals.toSorted((a, b) => a[0] - b[0])) {
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)
      continue;
    const previous = result.at(-1);
    if (previous && previous[1] >= start)
      previous[1] = Math.max(previous[1], end);
    else result.push([start, end]);
  }
  return result;
}
const minutes = (intervals: [number, number][]) =>
  merged(intervals).reduce((sum, [a, b]) => sum + (b - a) / 60000, 0);
const bounded = (
  span: Interval,
  start: number,
  end: number,
): [number, number] => [
  Math.max(start, time(span.startedAt)),
  Math.min(end, span.endedAt === null ? end : time(span.endedAt)),
];

export function materializeTeho(
  records: TehoRecords | undefined,
  asOf: string,
): Snapshots {
  const result: Snapshots = Object.fromEntries(
    Object.keys(TEHO_CONTRACTS).map((key) => [key, []]),
  );
  if (!records) return result;
  const cutoff = time(asOf);
  const index = <T extends { id: string }>(items: T[]) => {
    const map = new Map(items.map((item) => [item.id, item]));
    if (map.size !== items.length)
      throw new Error("Duplicate TEHO source identity");
    return map;
  };
  const cleaners = index(records.cleanerShifts),
    drivers = index(records.driverShifts),
    requests = index(records.requests);
  index(records.cleaningTasks);
  index(records.trips);
  index(records.waits);
  const base = (s: TehoShift, id = s.id): Row => ({
    id,
    operating_unit_id: s.operatingUnitId,
    property_id: s.propertyId,
    property_name: s.propertyName,
    work_date: s.workDate,
    snapshot_at: asOf,
  });
  const shiftRows = new Map<string, Row>();
  for (const [items, table] of [
    [records.cleanerShifts, "mv_cleaner_teho"],
    [records.driverShifts, "mv_driver_teho"],
  ] as const) {
    const employeeSpans = new Map<string, [number, number][]>();
    for (const s of items.toSorted(
      (a, b) => time(a.startedAt) - time(b.startedAt),
    )) {
      const start = time(s.startedAt),
        end = time(s.endedAt);
      if (!Number.isFinite(start)) throw new Error("Invalid TEHO shift start");
      if (start > cutoff) continue;
      const closed = Number.isFinite(end) && end > start && end <= cutoff;
      const spanEnd = closed ? end : cutoff;
      const prior = employeeSpans.get(s.employeeId) ?? [];
      if (prior.some(([a, b]) => start < b && spanEnd > a))
        throw new Error("Overlapping TEHO employee shifts");
      prior.push([start, spanEnd]);
      employeeSpans.set(s.employeeId, prior);
      const breaksValid = s.breaks.every(
        (b) =>
          Number.isFinite(time(b.startedAt)) &&
          (b.endedAt === null || time(b.endedAt) >= time(b.startedAt)),
      );
      const observedBreaks = (type: "meal" | "coffee") =>
        minutes(
          s.breaks
            .filter((b) => b.type === type)
            .map((b) => bounded(b, start, spanEnd)),
        );
      const row: Row = {
        ...base(s),
        employee_id: s.employeeId,
        employee_name: s.employeeName,
        started_at: s.startedAt,
        ended_at: closed ? s.endedAt : null,
        elapsed_minutes: closed ? (end - start) / 60000 : null,
        meal_minutes: observedBreaks("meal"),
        coffee_minutes: observedBreaks("coffee"),
        closed,
        evidence_complete:
          closed &&
          breaksValid &&
          s.breaks.every(
            (b) =>
              time(b.startedAt) >= spanEnd ||
              (b.endedAt !== null && time(b.endedAt) <= cutoff),
          ),
      };
      if ("hourlyRateCents" in s) {
        row.hourly_rate_cents = validCents(s.hourlyRateCents)
          ? s.hourlyRateCents
          : null;
        row.evidence_complete =
          !!row.evidence_complete && validCents(s.hourlyRateCents);
      } else {
        row.planned_km = validDistance(s.plannedKm) ? s.plannedKm : null;
        row.actual_km = closed && validDistance(s.actualKm) ? s.actualKm : null;
        row.evidence_complete =
          !!row.evidence_complete &&
          validDistance(s.plannedKm) &&
          validDistance(s.actualKm);
      }
      if (shiftRows.has(s.id))
        throw new Error("Duplicate TEHO shift identity across roles");
      result[table].push(row);
      shiftRows.set(s.id, row);
    }
  }
  // Task evidence is independent from payroll; it never replicates full-shift wages.
  const participantSpans = new Map<string, [number, number][]>();
  for (const task of records.cleaningTasks) {
    if (!task.participants.length)
      throw new Error("Cleaning price has no participants");
    const first = cleaners.get(task.participants[0].shiftId);
    if (!first) throw new Error("Unknown cleaner shift");
    if (!shiftRows.has(first.id) || time(task.startedAt) > cutoff) continue;
    const completed =
      Number.isFinite(time(task.endedAt)) &&
      time(task.endedAt) >= time(task.startedAt) &&
      time(task.endedAt) <= cutoff;
    const participantIds = new Set<string>();
    const allocations = task.participants.map((p) => {
      const s = cleaners.get(p.shiftId);
      if (
        !s ||
        s.propertyId !== first.propertyId ||
        s.workDate !== first.workDate ||
        participantIds.has(s.id)
      )
        throw new Error("Invalid cleaner task ownership");
      participantIds.add(s.id);
      const a = time(p.startedAt),
        b = time(p.endedAt);
      const valid =
        Number.isFinite(a) &&
        Number.isFinite(b) &&
        b > a &&
        b <= cutoff &&
        a >= time(s.startedAt) &&
        b <= time(s.endedAt) &&
        a >= time(task.startedAt) &&
        b <= time(task.endedAt);
      if (valid) {
        const prior = participantSpans.get(s.id) ?? [];
        if (prior.some(([x, y]) => a < y && b > x))
          throw new Error("Overlapping TEHO cleaner task sessions");
        prior.push([a, b]);
        participantSpans.set(s.id, prior);
      }
      return {
        ...base(s, JSON.stringify([task.id, s.id])),
        shift_id: s.id,
        task_id: task.id,
        elapsed_minutes: valid ? (b - a) / 60000 : 0,
        meal_minutes: valid
          ? minutes(
              s.breaks
                .filter((x) => x.type === "meal")
                .map((x) => bounded(x, a, b)),
            )
          : 0,
        eligible: valid && completed,
      } satisfies Row;
    });
    const priceValid = validCents(task.priceCents) && task.currency === "EUR";
    const totalPaidMinutes = allocations.reduce(
      (sum, p) => sum + Number(p.elapsed_minutes) - Number(p.meal_minutes),
      0,
    );
    if (
      completed &&
      (!priceValid ||
        totalPaidMinutes <= 0 ||
        allocations.some((p) => !p.eligible))
    ) {
      for (const p of allocations) {
        const s = shiftRows.get(String(p.shift_id));
        if (s) s.evidence_complete = false;
      }
    }
    let priorMinutes = 0;
    for (const allocation of allocations) {
      (allocation as Row).prior_paid_task_minutes = priorMinutes;
      priorMinutes +=
        Number(allocation.elapsed_minutes) - Number(allocation.meal_minutes);
    }
    result.mv_teho_cleaning_tasks.push({
      ...base(first, task.id),
      unit_name: task.unitName,
      price_cents: priceValid ? task.priceCents : null,
      currency: task.currency,
      completed,
      total_paid_task_minutes: totalPaidMinutes,
    });
    result.mv_teho_cleaner_allocations.push(...allocations);
  }
  for (const p of result.mv_teho_cleaner_allocations)
    p.eligible =
      !!p.eligible && !!shiftRows.get(String(p.shift_id))?.evidence_complete;

  const tripGroups = new Map<string, DriverTrip[]>();
  for (const trip of records.trips) {
    if (!requests.has(trip.requestId)) throw new Error("Unknown trip request");
    const items = tripGroups.get(trip.requestId) ?? [];
    items.push(trip);
    tripGroups.set(trip.requestId, items);
  }
  for (const request of records.requests) {
    const s = drivers.get(request.shiftId);
    if (!s) throw new Error("Unknown driver shift");
    if (!shiftRows.has(s.id) || time(request.startedAt) > cutoff) continue;
    const start = time(request.startedAt),
      end = time(request.endedAt),
      deadline = time(request.deadline);
    const eligible = !request.cancelled;
    const completed =
      eligible && Number.isFinite(end) && end >= start && end <= cutoff;
    const rawTrips = tripGroups.get(request.id) ?? [];
    const trips = rawTrips
      .filter((t) => time(t.startedAt) <= cutoff)
      .toSorted(
        (a, b) =>
          time(a.startedAt) - time(b.startedAt) || a.id.localeCompare(b.id),
      );
    const evidence =
      Number.isFinite(start) &&
      start >= time(s.startedAt) &&
      start <= time(s.endedAt) &&
      Number.isFinite(deadline) &&
      deadline >= start &&
      request.waitingObserved &&
      rawTrips.every((t) => Number.isFinite(time(t.startedAt))) &&
      (request.endedAt === null ||
        (Number.isFinite(end) &&
          end >= start &&
          (!shiftRows.get(s.id)!.closed || end <= time(s.endedAt)))) &&
      (!completed || trips.length > 0) &&
      trips.every(
        (t, i) =>
          validDistance(t.kilometres) &&
          time(t.startedAt) >= start &&
          time(t.endedAt) >= time(t.startedAt) &&
          time(t.endedAt) <= cutoff &&
          time(t.endedAt) <= time(s.endedAt) &&
          (!i || time(t.startedAt) >= time(trips[i - 1].endedAt)),
      );
    const due = eligible && Number.isFinite(deadline) && deadline <= cutoff;
    result.mv_driver_requests.push({
      ...base(s, request.id),
      shift_id: s.id,
      created_at: request.startedAt,
      completed_at: completed ? request.endedAt : null,
      deadline: Number.isFinite(deadline) ? request.deadline : null,
      completed,
      due,
      on_time: due && completed && end <= deadline,
      urgent: request.urgent,
      dog_help: request.dogHelp,
      cancelled: request.cancelled,
      evidence_complete: evidence,
    });
    if (eligible && !evidence) shiftRows.get(s.id)!.evidence_complete = false;
    trips.forEach((t, i) =>
      result.mv_driver_trips.push({
        ...base(s, t.id),
        request_id: request.id,
        started_at: t.startedAt,
        ended_at: time(t.endedAt) <= cutoff ? t.endedAt : null,
        attempt_number: i + 1,
        kilometres: validDistance(t.kilometres) ? t.kilometres : null,
      }),
    );
  }
  // Remove already covered time across requests for the same cleaner. Earliest
  // waiting episode owns overlapping time; ties use the episode identity.
  const covered = new Map<string, [number, number][]>();
  for (const wait of records.waits.toSorted(
    (a, b) => time(a.startedAt) - time(b.startedAt) || a.id.localeCompare(b.id),
  )) {
    const request = requests.get(wait.requestId);
    if (!request) throw new Error("Unknown waiting request");
    const s = drivers.get(request.shiftId)!;
    if (
      !shiftRows.has(s.id) ||
      time(wait.startedAt) > cutoff ||
      request.cancelled ||
      time(request.startedAt) > cutoff
    )
      continue;
    const end = Math.min(
      cutoff,
      Number.isFinite(time(s.endedAt)) ? time(s.endedAt) : cutoff,
      Number.isFinite(time(request.endedAt)) ? time(request.endedAt) : cutoff,
    );
    const interval = bounded(
      wait,
      Math.max(time(s.startedAt), time(request.startedAt)),
      end,
    );
    if (
      !Number.isFinite(time(wait.startedAt)) ||
      (wait.endedAt !== null &&
        (!Number.isFinite(time(wait.endedAt)) ||
          time(wait.endedAt) < time(wait.startedAt)))
    ) {
      shiftRows.get(s.id)!.evidence_complete = false;
      continue;
    }
    if (interval[1] <= interval[0]) continue;
    const key = wait.cleanerId;
    const existing = covered.get(key) ?? [];
    let pieces: [number, number][] = [interval];
    for (const [a, b] of existing)
      pieces = pieces.flatMap(([x, y]) =>
        b <= x || a >= y
          ? [[x, y]]
          : ([
              [x, Math.min(a, y)],
              [Math.max(b, x), y],
            ].filter(([m, n]) => n > m) as [number, number][]),
      );
    pieces.forEach(([a, b], i) =>
      result.mv_cleaner_waits.push({
        ...base(s, `${wait.id}/${i}`),
        request_id: wait.requestId,
        cleaner_id: wait.cleanerId,
        started_at: new Date(a).toISOString(),
        ended_at: new Date(b).toISOString(),
        wait_minutes: (b - a) / 60000,
      }),
    );
    covered.set(key, merged([...existing, interval]));
  }
  return result;
}

export function createTehoRecords(
  properties: { id: string; name: string }[],
  operatingUnitId: string,
  dateOffset: (days: number) => string,
): TehoRecords {
  const data: TehoRecords = {
    cleanerShifts: [],
    cleaningTasks: [],
    driverShifts: [],
    requests: [],
    trips: [],
    waits: [],
  };
  const stamp = (date: string, minute: number) =>
    new Date(
      Date.parse(date + "T00:00:00Z") + (minute - 180) * 60000,
    ).toISOString();
  const names = [
    "Emma Virtanen",
    "Lina Korhonen",
    "Amir Hassan",
    "Sofia Laine",
    "Noah Niemi",
    "Anna Koski",
    "Elias Aalto",
    "Sara Lehto",
    "Leo Mäkinen",
  ];
  // One stable roster across the whole operating unit, split 3/3/2 by property.
  const driverNames = [
    "Mika Lehtinen",
    "Omar Ali",
    "Anni Kallio",
    "Pavel Novak",
    "Ella Koskinen",
    "Danylo Melnyk",
    "Jussi Heikkinen",
    "Aino Salmi",
  ];
  properties.forEach((property, p) => {
    for (let day = -89; day <= 0; day++) {
      const date = dateOffset(day),
        seed = day + 89 + p * 11;
      const baseShift = (
        id: string,
        employeeId: string,
        employeeName: string,
      ): TehoShift => ({
        id,
        employeeId,
        employeeName,
        propertyId: property.id,
        propertyName: property.name,
        operatingUnitId,
        workDate: date,
        startedAt: stamp(date, 480),
        endedAt: stamp(date, 960),
        breaks: [
          {
            type: "meal",
            startedAt: stamp(date, 720),
            endedAt: stamp(date, 750),
          },
          {
            type: "coffee",
            startedAt: stamp(date, 600),
            endedAt: stamp(date, 615),
          },
        ],
      });
      const cleaners = Array.from({ length: 3 }, (_, i): CleanerShift => ({
        ...baseShift(`cs-${p}-${day}-${i}`, `tc-${p}-${i}`, names[p * 3 + i]),
        hourlyRateCents: 1900 + i * 150 + p * 50,
      }));
      data.cleanerShifts.push(...cleaners);
      for (let slot = 0; slot < 4; slot++) {
        for (let person = 0; person < 3; person++) {
          if (slot === 0 && person === 1) continue;
          const start = [480, 570, 660, 810][slot],
            duration = 55 + ((seed + slot + person) % 4) * 10;
          const pair = slot === 0 && person === 0;
          data.cleaningTasks.push({
            id: `ct-${p}-${day}-${slot}-${person}`,
            unitName: `${["NC", "PL", "HH"][p]}-${String(slot * 3 + person + 1).padStart(3, "0")}`,
            priceCents:
              (seed % 17 === 0 ? 2500 : 6200) +
              slot * 700 +
              person * 350 +
              (pair ? 2800 : 0),
            currency: "EUR",
            startedAt: stamp(date, start),
            endedAt: stamp(date, start + duration),
            participants: [person, ...(pair ? [1] : [])].map((i) => ({
              shiftId: cleaners[i].id,
              startedAt: stamp(date, start + (i === 1 && pair ? 15 : 0)),
              endedAt: stamp(date, start + duration),
            })),
          });
        }
      }
      const weekday = new Date(date + "T12:00:00Z").getUTCDay();
      if (weekday === 0 || weekday === 6) continue;
      for (
        let driver = p;
        driver < driverNames.length;
        driver += properties.length
      ) {
        const s: DriverShift = {
          ...baseShift(
            `ds-${p}-${day}-${driver}`,
            `td-${driver}`,
            driverNames[driver],
          ),
          plannedKm: 0,
          actualKm: 0,
        };
        data.driverShifts.push(s);
        let clock = 485;
        for (let n = 0; n < 24 + ((seed + driver) % 3); n++) {
          const repeated = (seed + n + driver) % 7 === 0,
            urgent = n % 4 === 0,
            dogHelp = (n + seed) % 5 === 0;
          const distance = 2 + ((n + seed) % 4);
          const tripCount = repeated ? 2 : 1;
          const duration = 10 + (dogHelp ? 4 : 0) + (repeated ? 8 : 0);
          // Fit sequential tasks around coffee and lunch within the paid shift.
          for (const [start, end] of [
            [600, 615],
            [720, 750],
          ])
            if (clock < end && clock + duration > start) clock = end;
          const end = clock + duration,
            id = `dr-${p}-${day}-${driver}-${n}`;
          data.requests.push({
            id,
            shiftId: s.id,
            startedAt: stamp(date, clock),
            endedAt: stamp(date, end),
            deadline: stamp(date, clock + (urgent ? 20 : 28)),
            urgent,
            dogHelp,
            cancelled: false,
            waitingObserved: true,
          });
          for (let attempt = 0; attempt < tripCount; attempt++)
            data.trips.push({
              id: `${id}/trip/${attempt}`,
              requestId: id,
              startedAt: stamp(date, clock + (attempt ? 8 : 0)),
              endedAt: stamp(date, attempt === tripCount - 1 ? end : clock + 8),
              kilometres: distance + (seed % 3) * 0.4,
            });
          s.plannedKm! += distance;
          s.actualKm! += tripCount * (distance + (seed % 3) * 0.4);
          if ((seed + n) % 3 === 0)
            data.waits.push({
              id: `${id}/wait`,
              requestId: id,
              cleanerId: `tc-${p}-${(n + driver) % 3}`,
              startedAt: stamp(date, clock + 2),
              endedAt: stamp(
                date,
                Math.min(end, clock + 5 + ((seed + n) % 16)),
              ),
            });
          clock = end + 3;
        }
      }
    }
  });
  return data;
}
